import { Dialog } from '@mui/material';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React from 'react';

import { useDownloadFileInfoQuery } from '@/api/v1';
import { EmptyFieldValueRenderer } from '@/components/molecules/EmptyFieldValueRenderer';
import { FilePreview } from '@/components/molecules/FilePreview';
import { useTranslator } from '@/hooks/useTranslator';
import { FileField, ValueType } from '@/types/fields';
import { formatFileSize } from '@/utility/formatFileSize';

interface Props {
  readonly value: ValueType;
  readonly field: FileField<unknown>;
}

export const FileFieldValueRenderer: React.FC<Props> = (props: Props): React.ReactElement => {
  const { value, field } = props;
  const { path } = field;
  const { data: metadata = null, isFetching: fetching } = useDownloadFileInfoQuery(
    { path: path, id: String(value) },
    { skip: !path || value === '' },
  );
  const [previewing, setPreviewing] = React.useState<boolean>(false);
  const tr = useTranslator();

  const formattedValue = React.useMemo((): string | null => {
    if (typeof value !== 'string') {
      return null;
    }
    return tr(
      'FILE_NAME_AND_SIZE_TEMPLATE',
      metadata?.name ?? '',
      formatFileSize(metadata?.size ?? 0),
    );
  }, [metadata?.name, metadata?.size, tr, value]);

  const toggleShowPreview = React.useCallback((): void => {
    setPreviewing((showing: boolean): boolean => !showing);
  }, []);

  if (formattedValue === null) {
    console.debug('file value renderer value is not a string', value);
    return <EmptyFieldValueRenderer />;
  }

  if (fetching) {
    return (
      <Typography variant="body2" color="custom.DividerLight">
        {tr('LOADING_PLEASE_WAIT')}&hellip;
      </Typography>
    );
  }

  return (
    <>
      <Typography variant="body2" color="text.primary">
        <Typography component="span" fontSize="inherit" fontWeight="inherit">
          {formattedValue}
        </Typography>
        <Typography component="span" fontSize="inherit" fontWeight="inherit">
          {' '}
        </Typography>
        <Typography
          component={Link}
          fontSize="inherit"
          fontWeight="inherit"
          color="primary.main"
          onClick={toggleShowPreview}>
          {tr('PREVIEW')}
        </Typography>
      </Typography>
      <Dialog
        open={previewing}
        title={`Preview of ${metadata?.name}`}
        fullScreen={true}
        onClose={toggleShowPreview}>
        {/* fetch the file and preview it */}
        <FilePreview path={path} id={String(value)} onClose={toggleShowPreview} />
      </Dialog>
    </>
  );
};
